import logger from '../logger';
import profiler from './profiler';
import JobQueue from './job-queue';
import { subscribable, x } from '../utils';

const log = logger.get('execution-engine');
const DEFAULT_MAX_CONCURRENT_REQUESTS = 30;

/**
 * Keeps track on the number of pending jobs
 *
 * If the number of pending jobs exceeds maxConcurrentRequests, isFull() will return true.
 * It is each queue´s responsibility to enqueue jobs when pendingJobs is full
 *
 * @param {number} maxConcurrentReq - Max concurrent requests value.
 *
 */
function PendingJobs(maxConcurrentReq) {
    const self = this;
    const queue = [];

    let maxConcurrentRequests = maxConcurrentReq || DEFAULT_MAX_CONCURRENT_REQUESTS;
    let counter = 0;
    let peak = 0;

    this.setMaxConcurrentRequests = max => (maxConcurrentRequests = max);

    this.getMaxConcurrentRequests = () => maxConcurrentRequests;

    this.increase = () => counter++;

    this.decrease = () => {
        counter--;
        if (queue.length > 0 && !this.isFull()) {
            const pendingJob = queue.shift();
            log.debug(`PENDING JOBS QUEUE - dequeue: ${pendingJob.jobDescription}`);
            pendingJob.execute();
        }
    };

    this.isFull = () => maxConcurrentRequests > 0 && counter >= maxConcurrentRequests;

    this.enqueue = pendingJob => {
        queue.push(pendingJob);
        if (queue.length > peak) {
            peak = queue.length;
        }
        log.debug(`PENDING JOBS QUEUE - enqueue: ${pendingJob.jobDescription}`);
    };

    this.instrumentation = () => ({
        __name__: 'Pending Jobs',
        __description__:
            'A module inside the Platform execution engine to handle max number of concurrent requests',
        __this__: true,

        getQueueSize: () => queue.length,

        getMaxConcurrentRequests: self.getMaxConcurrentRequests,

        reset: () => (peak = 0),

        getPeak: () => peak,
    });
}

/**
 * Keeps track on the number of queued jobs (including jobs that is currently running)
 *
 * if number of queued jobs decreases to zero, "idle" will pe published
 */
function QueuedJobs() {
    let counter = 0;
    subscribable.mixin(this);

    this.increase = () => counter++;

    this.decrease = () => {
        counter--;
        if (counter === 0) {
            this.publish('idle');
        }
    };

    this.isEmpty = () => counter === 0;

    this.instrumentation = () => ({
        __name__: 'Queued Jobs',
        __description__:
            'A module inside the execution engine to prevent concurrent requests on same collection/id',
        getCount: () => counter,
    });
}

/**
 * This module is responsible for queueing network operations and is used by
 * the repository and Socket Enabler Client.
 * As for now, by network operations we mean all: 'get', 'subscribe' and 'fetch'
 * We will queue all 'get' and 'subscribe' even if they will not hit the network.
 *
 * This will guarantee that there is only one operation active at a time per endpoint (collection/id)
 *
 * There will be one queue per collection/id. I.e. two subscribe on different id's
 * will still run in parallel.
 */
function ExecutionEngine() {
    let pendingJobs = new PendingJobs();
    let queuedJobs = new QueuedJobs();
    let queueMap = {};
    let globalJobCounter = 0;
    let globalWaitCounter = 0;
    let maxConcurrentRequests;

    this.init = config => {
        maxConcurrentRequests = config.maxConcurrentRequests || DEFAULT_MAX_CONCURRENT_REQUESTS;
        pendingJobs = new PendingJobs(maxConcurrentRequests);
    };

    this.getMaxConcurrentRequests = () => pendingJobs.getMaxConcurrentRequests();

    this.setMaxConcurrentRequests = max => pendingJobs.setMaxConcurrentRequests(max);

    this.profiler = profiler;

    /**
     * This will add the jobDescription object on the queue associated with the collection
     * and identifier parameter.
     *
     * this function will decorate the jobDescription object with a few more attributes:
     *   async: will be set to true if the 'job' attribute has a 'done' argument defined
     *   jobNumber: a unique id
     *   toString: to be able to print a jobDescription
     *
     *   @param {string} collection - collection
     *   @param {string} identifier - identifier of a job
     *   @param {{}} jobDescription - an object describing the job
     *
     *      Example 1:
     *      jobDescription = {
     *          job: function (done) {...},
     *          info: 'FETCH'
     *      }
     *      If a 'done' argument is provided in the job function,
     *      the job will be treated as an asynchronous job. The
     *      job will not be removed from the queue (will block the
     *      queue) until 'done' is called.
     *
     *      Example 2:
     *      jobDescription = {
     *          job: function () {...},
     *          info: 'SOMETHING SYNCHRONOUS'
     *      }
     *      If a 'done' argument is NOT provided in the job function,
     *      the job will be treated as a synchronous job. The
     *      job will not be removed from the queue as soon as the 'job' function
     *      returns.
     *
     */
    this.addJob = (collection, identifier, jobDescription) => {
        globalJobCounter += 1;
        if (jobDescription.job.length > 0) {
            jobDescription.async = true;
        }
        jobDescription.jobNumber = globalJobCounter;
        jobDescription.collection = collection;
        jobDescription.toString = () =>
            `JOB ${jobDescription.jobNumber} : ${jobDescription.info} (${collection}:${identifier})`;

        makeSureQueueExists(collection, identifier);
        queueMap[collection][identifier].enqueue(jobDescription);
    };

    /**
     * If collection and identifier are provided, a synchronous job will
     * be put on the associated queue, and callback will be called when that
     * job runs.
     *
     * If collection but not id is provided, a job will be added to all (non empty)
     * queues under the collection 'collection' (Remember, there is one queue instance for
     * each id under a collection). When all the jobs has run, callback will be
     * called.
     *
     * If not collection nor id is provided, a job will be added to all (non empty) queues,
     * and when all these jobs has run, callback will be called.
     *
     * @param {string} collection - collection
     * @param {string} identifier - identifier
     * @param {function} callback - callback
     */
    this.wait = (collection, identifier, callback) => {
        if (callback) {
            waitOnIdentifier(collection, identifier, callback);
        } else if (identifier) {
            callback = identifier;
            waitOnCollection(collection, callback);
        } else {
            callback = collection;
            if (!callback) {
                throw new Error('Missing callback');
            }
            waitOnAll(callback);
        }
    };

    this.waitForMany = (pairs, callback) => {
        const onlyCollection = [];
        const collectionAndId = [];

        if (!callback) {
            throw new Error('Missing callback');
        }

        if (pairs.length === 0) {
            setTimeout(callback, 0);
            return;
        }

        pairs.forEach(pair => {
            if (!pair.collection) {
                throw new Error('Missing collection property');
            }
            if (pair.identifier) {
                collectionAndId.push(pair);
            } else {
                onlyCollection.push(pair);
            }
        });

        const triggerCallback = x.after(pairs.length, callback);
        onlyCollection.forEach(pair => waitOnCollection(pair.collection, triggerCallback));
        collectionAndId.forEach(pair =>
            waitOnIdentifier(pair.collection, pair.identifier, triggerCallback)
        );
    };

    /**
     * This will notify (call 'callback') after the queue has been idle for at least 'millis'
     * milliseconds (to be precise if 'callback' is invoked then the queue has been idle
     * between 'millis' and 2*'millis' milliseconds).
     *
     * @param {function} callback - Callback function.
     * @param {number} millis - Milliseconds to be idle before calling callback.
     */
    this.waitForIdle = (callback, millis) => {
        millis = millis || 0;

        const onIdle = () => {
            const currentJobCounter = globalJobCounter;
            sub.dispose();

            setTimeout(() => {
                if (globalJobCounter === currentJobCounter) {
                    callback();
                } else {
                    this.waitForIdle(callback, millis);
                }
            }, millis);
        };

        const sub = queuedJobs.subscribe('idle', onIdle);

        if (queuedJobs.isEmpty()) {
            onIdle();
        }
    };

    this.instrumentation = function () {
        const instrumentation = {
            __name__: 'Execution Engine',
            __description__: 'A module to run/queue requests',
            __this__: true,
        };

        instrumentation.getQueueMap = () => queueMap;
        instrumentation.getPendingJobs = () => pendingJobs;
        instrumentation.getQueuedJobs = () => queuedJobs;
        instrumentation.getNonEmptyQueuesOnCollection = getNonEmptyQueuesOnCollection;
        instrumentation.getAllNonEmptyQueues = getAllNonEmptyQueues;
        instrumentation.getNbrOfQueuedJobs = () =>
            getAllNonEmptyQueues().reduce(
                (accumulator, queue) => accumulator + queue.instrumentation().size,
                0
            );

        instrumentation.getNbrOfQueuedJobsWithoutWait = () =>
            getAllNonEmptyQueues().reduce(
                (accumulator, queue) =>
                    accumulator + queue.instrumentation().getSizeWithoutWaitJobs(),
                0
            );

        instrumentation.getGlobalJobCounter = () => globalJobCounter;

        instrumentation.init = () => {
            pendingJobs = new PendingJobs();
            queuedJobs = new QueuedJobs();
            queueMap = {};
        };

        return instrumentation;
    };

    function makeSureQueueExists(collection, identifier) {
        if (!queueMap[collection]) {
            queueMap[collection] = {};
        }
        if (!queueMap[collection][identifier]) {
            queueMap[collection][identifier] = new JobQueue(
                collection,
                identifier,
                pendingJobs,
                queuedJobs
            );
        }
    }

    function waitOnIdentifier(collection, identifier, callback) {
        makeSureQueueExists(collection, identifier);
        const jobDescription = createWaitJob(callback);
        queueMap[collection][identifier].enqueue(jobDescription);
    }

    function waitOnCollection(collection, callback) {
        const queues = getNonEmptyQueuesOnCollection(collection);
        waitOnQueues(queues, callback);
    }

    function waitOnAll(callback) {
        const queues = getAllNonEmptyQueues();
        waitOnQueues(queues, callback);
    }

    function waitOnQueues(queues, callback) {
        const nbrOfQueues = queues.length;

        if (nbrOfQueues === 0) {
            callback();
            return;
        }

        const trigger = x.after(nbrOfQueues, callback);

        queues.forEach(queue => queue.enqueue(createWaitJob(trigger)));
    }

    function getNonEmptyQueuesOnCollection(collection) {
        const collectionMap = queueMap[collection];
        const queues = [];

        if (!collectionMap) {
            return queues;
        }

        Object.keys(collectionMap).forEach(key => {
            const queue = collectionMap[key];
            if (queue && !queue.isEmpty()) {
                queues.push(queue);
            }
        });

        return queues;
    }

    function getAllNonEmptyQueues() {
        return Object.keys(queueMap).reduce(
            (accumulator, collection) =>
                accumulator.concat(getNonEmptyQueuesOnCollection(collection)),
            []
        );
    }

    function createWaitJob(callback) {
        if (!callback) {
            throw new Error('Missing callback');
        }

        globalWaitCounter++;

        return {
            job: callback,
            jobNumber: globalWaitCounter,
            toString: () => `WAIT ${globalJobCounter}`,
        };
    }
}

export default ExecutionEngine;
